import { glob, isGlob } from '../glob'
import { getHash, getSource, normalize } from './lambda'

import type { GlobOptions } from '../glob'
import type { TIterator } from '../types'
import type { PathLikes, PathParts } from './types'

let globalJoinMode = false
const globalHashCache = new Map<string, Path>()
const globalSourceCache = new Map<string, Path>()

export class Path {
  declare initialParts: PropertyKey[]
  declare isAbsolute: boolean
  declare isRelative: boolean
  declare isRoot: boolean
  declare lastPart: PropertyKey
  declare parts: PathParts
  declare source: string
  get cache(): Map<string, Path> {
    return (this._cache ??= new Map())
  }

  /**
   * @deprecated
   * 向后兼容逻辑
   */
  get current() {
    return this
  }

  get length(): number {
    return this.parts.length
  }

  get parent(): Path | undefined {
    if (this.isRoot) return
    return (this._parent ??= new Path(this.initialParts))
  }

  declare private _cache: Map<string, Path>

  declare private _parent?: Path

  constructor(path?: PathLikes, base?: PathLikes) {
    const prevJoinMode = globalJoinMode
    globalJoinMode = false
    if (base) return new Path(base).resolve(path)
    if (path instanceof Path) return path
    const hash = getHash(path, prevJoinMode ? 'join' : undefined)
    const hashCache = globalHashCache.get(hash)
    if (hashCache) return hashCache
    const parts = normalize([path], !prevJoinMode)
    const source = getSource(parts, prevJoinMode ? '.' : '/')
    const sourceCache = globalSourceCache.get(source)
    if (sourceCache) {
      globalHashCache.set(hash, sourceCache)
      return sourceCache
    }
    this.source = source
    this.initialParts = parts.slice(0, -1)
    this.isAbsolute = !!parts.absolute
    this.isRelative = !parts.absolute
    this.isRoot = parts.length === 0
    this.lastPart = parts.at(-1)!
    this.parts = parts
    globalHashCache.set(hash, this)
    globalSourceCache.set(source, this)
  }

  static deleteIn(target?: any, path?: PathLikes): boolean {
    return new Path(path).deleteIn(target)
  }

  static ensureIn(target?: any, path?: PathLikes, value?: any): any {
    return new Path(path).ensureIn(target, value)
  }

  static existIn(target?: any, path?: PathLikes): boolean {
    return new Path(path).existIn(target)
  }

  static getIn(target?: any, path?: PathLikes): any {
    return new Path(path).getIn(target)
  }

  static join(...paths: PathLikes[]): Path {
    globalJoinMode = true
    return new Path(paths)
  }

  static normalize(...paths: PathLikes[]): Path {
    globalJoinMode = true
    return new Path(paths)
  }

  static relative(from: PathLikes, to: PathLikes): Path {
    return new Path(from).relative(to)
  }

  static resolve(...paths: PathLikes[]): Path {
    return new Path(paths)
  }

  static setIn(target?: any, path?: PathLikes, value?: any): any {
    return new Path(path).setIn(target, value)
  }

  static startsWith(pathA?: any, pathB?: PathLikes) {
    return new Path(pathA).startsWith(pathB)
  }

  deleteIn(target?: any): boolean {
    target = this.parent?.getIn(target)
    if (typeof target !== 'object' || target === null) return false
    delete target[this.lastPart]
    return true
  }

  ensureIn(target?: any, value?: any): any {
    const result = this.getIn(target)
    if (typeof result !== 'object' && result !== null) return result
    return this.setIn(target, value)
  }

  existIn(target?: any): boolean {
    const result = this.getIn(target)
    return result !== undefined && result !== null
  }

  getIn(target?: any): any {
    for (const part of this.parts) {
      if (typeof target !== 'object' || target === null) return undefined
      target = target[part]
    }
    return target
  }

  join(paths: PathLikes): Path {
    const hash = getHash(paths, 'join')
    const cache = this.cache.get(hash)
    if (cache) return cache
    const path = Path.join(this.source, paths)
    this.cache.set(hash, path)
    return path
  }

  match(pattern: PathLikes, options: GlobOptions = {}): boolean {
    if (isGlob(pattern)) {
      options.cwd ??= this.source ?? options?.cwd
      return glob(pattern, this.source, options)
    } else return new Path(this.source) === new Path(pattern)
  }

  relative(paths: PathLikes): Path {
    const hash = getHash(paths, 'relative')
    const cache = this.cache.get(hash)
    if (cache) return cache
    const from = this.parts
    const to = new Path(paths).parts
    const minLen = Math.min(from.length, from.length)
    let sameLen = 0
    while (sameLen < minLen) {
      if (from[sameLen] === to[sameLen]) {
        sameLen++
      } else {
        break
      }
    }
    const parts = from.slice(sameLen).fill('..').concat(to.slice(sameLen))
    const path = Path.join(parts)
    this.cache.set(hash, path)
    return path
  }

  resolve(paths: PathLikes): Path {
    const hash = getHash(paths, 'resolve')
    const cache = this.cache.get(hash)
    if (cache) return cache
    const path = new Path([this.source, paths])
    this.cache.set(hash, path)
    return path
  }

  setIn(target?: any, value?: any): any {
    if (typeof target !== 'object' || target === null) return target
    for (let i = 0; i < this.length - 1; i++)
      target = target[this.parts[i]] ??= isNaN(this.parts[i + 1]) ? {} : []
    target[this.lastPart] = value
    return target[this.lastPart]
  }

  startsWith(path: PathLikes): boolean {
    return this.source.startsWith(new Path(path).source)
  }

  [Symbol.iterator](): TIterator<PropertyKey> {
    return this.parts[Symbol.iterator]()
  }

  toJS() {
    return this.source
  }

  toJSON() {
    return this.source
  }

  toString() {
    return this.source
  }
}
